// libraries.
import { useEffect } from "react";
import { Modal, Form, Button, Spinner, Row, Col } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { electricPurple500, monserratSemiBold } from "@habitech/shared";

// interfaces.
import { ExceptionRule } from "../rulesData";
import {
  RawRuleItem,
  RawConditionSimple,
  RawConditionOperator,
} from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";

// styles.
import { StyledRulesModalForm } from "../styles";

// constants.
import { CONTEXT_VARIABLES, CONDITION_OPERATORS } from "./rulesFormConstants";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  exception: ExceptionRule | null;
  rawRule: RawRuleItem | null;
  country: string;
  onClose: () => void;
}

interface FormValues {
  name: string;
  description: string;
  priority: number;
  isActive: boolean;
  conditionLeft: string;
  conditionType: RawConditionOperator;
  conditionRight: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EditExceptionModal = ({
  exception,
  rawRule,
  country,
  onClose,
}: Props) => {
  const { saveException, isSaving } = useRulesSlice();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    if (exception && rawRule) {
      const condition = rawRule.rule.condition as RawConditionSimple;
      reset({
        name: rawRule.rule.name,
        description: rawRule.rule.description,
        priority: rawRule.rule.priority,
        isActive: rawRule.is_active === "active",
        conditionLeft: condition.left,
        conditionType: condition.type,
        conditionRight: String(condition.right),
      });
    }
  }, [exception, rawRule, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!exception || !rawRule) return;

    const conditionRightValue = isNaN(Number(data.conditionRight))
      ? data.conditionRight
      : Number(data.conditionRight);

    const updatedCondition: RawConditionSimple = {
      left: data.conditionLeft,
      type: data.conditionType,
      right: conditionRightValue,
    };

    const updatedRule = {
      ...rawRule.rule,
      id: rawRule.rule_id,
      name: data.name,
      description: data.description,
      priority: data.priority,
      condition: updatedCondition,
    };

    const updatedException: ExceptionRule = {
      ...exception,
      name: data.name,
      description: data.description,
    };

    // is_active: pendiente definir endpoint (PUT vs PATCH)
    await saveException(
      updatedException,
      updatedRule,
      country,
      rawRule.rule_id,
    );
    onClose();
  };

  return (
    <Modal show={exception !== null} onHide={onClose} centered>
      <Modal.Header
        closeButton
        style={{ borderBottom: `2px solid ${electricPurple500}` }}
      >
        <Modal.Title
          style={{ fontFamily: monserratSemiBold, fontWeight: 600, fontSize: 16 }}
        >
          Editar excepción
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <StyledRulesModalForm className="d-flex flex-column gap-3">

            <Row className="g-3">
              <Col xs={9}>
                <Form.Group>
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Nombre de la excepción"
                    {...register("name", { required: "Requerido" })}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={3}>
                <Form.Group>
                  <Form.Label>Prioridad</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={0}
                    {...register("priority", {
                      required: "Requerido",
                      valueAsNumber: true,
                      min: { value: 0, message: "≥ 0" },
                    })}
                    isInvalid={!!errors.priority}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.priority?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                placeholder="Describe cuándo aplica esta excepción"
                {...register("description", { required: "Requerido" })}
                isInvalid={!!errors.description}
              />
              <Form.Control.Feedback type="invalid">
                {errors.description?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div>
              <Form.Label>Condición</Form.Label>
              <Row className="g-2">
                <Col xs={5}>
                  <Form.Select
                    size="sm"
                    {...register("conditionLeft", { required: "Requerido" })}
                    isInvalid={!!errors.conditionLeft}
                  >
                    {CONTEXT_VARIABLES.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={4}>
                  <Form.Select
                    size="sm"
                    {...register("conditionType", { required: "Requerido" })}
                    isInvalid={!!errors.conditionType}
                  >
                    {CONDITION_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={3}>
                  <Form.Control
                    size="sm"
                    placeholder="Valor"
                    {...register("conditionRight", { required: "Requerido" })}
                    isInvalid={!!errors.conditionRight}
                  />
                </Col>
              </Row>
            </div>

            <Form.Group>
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Estado actual:</Form.Label>
                <span className={`badge-status ${watch("isActive") ? "active" : "inactive"}`}>
                  {watch("isActive") ? "● Activa" : "○ Inactiva"}
                </span>
              </div>
              <Form.Text className="d-block text-muted mt-1" style={{ fontSize: 11 }}>
                Para cambiar el estado usá el botón Activar / Desactivar en la tabla.
              </Form.Text>
            </Form.Group>

          </StyledRulesModalForm>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #eee" }}>
          <Button
            className="btn-habi-white"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button type="submit" className="btn-habi" disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" animation="border" />
            ) : (
              "Guardar cambios"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
